/**
 * TranscriptPanel Component
 * 
 * Displays live transcription during telehealth calls.
 * Clean, minimal design inspired by Google Meet captions.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, Mic, Stethoscope, User } from 'lucide-react';
import { TranscriptLine } from './VideoRoom';

interface TranscriptPanelProps {
  lines: TranscriptLine[];
  isActive: boolean;
  onClose?: () => void;
}

const TranscriptPanel: React.FC<TranscriptPanelProps> = ({
  lines,
  isActive,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, autoScroll]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    setAutoScroll(isAtBottom);
  };

  const isProvider = (name: string) => 
    name.toLowerCase().includes('provider') || 
    name.toLowerCase().includes('doctor') ||
    name.toLowerCase().includes('dr.');

  const finalLines = lines.filter(l => l.isFinal);
  const wordCount = finalLines.reduce((acc, l) => acc + l.text.split(' ').length, 0);

  return (
    <div className="flex h-full flex-col bg-[#1a1a1a]">
      {/* Transcript content */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3"
        onScroll={handleScroll}
      >
        {lines.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#2d2d2d]">
              <Mic className="h-5 w-5 text-[#8ab4f8]" />
            </div>
            <p className="text-sm text-[#9aa0a6]">Waiting for speech...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {lines.map((line, index) => {
              const showHeader = index === 0 || lines[index - 1].speaker !== line.speaker;
              const provider = isProvider(line.speakerName);
              
              return (
                <div key={line.id}>
                  {showHeader && (
                    <div className="mb-1 flex items-center gap-1.5">
                      <div className={`flex h-5 w-5 items-center justify-center rounded-full ${provider ? 'bg-[#81c995]/20' : 'bg-[#f28b82]/20'}`}>
                        {provider ? (
                          <Stethoscope className="h-2.5 w-2.5 text-[#81c995]" />
                        ) : (
                          <User className="h-2.5 w-2.5 text-[#f28b82]" />
                        )}
                      </div>
                      <span className={`text-xs font-medium ${provider ? 'text-[#81c995]' : 'text-[#f28b82]'}`}>
                        {line.speakerName}
                      </span>
                      <span className="text-[10px] text-[#5f6368]">
                        {new Date(line.timestamp).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                  )}
                  <p className={`pl-7 text-[13px] leading-relaxed ${line.isFinal ? 'text-[#e8eaed]' : 'text-[#9aa0a6] italic'}`}>
                    {line.text}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Scroll to bottom */}
      {!autoScroll && lines.length > 0 && (
        <div className="px-4 pb-2">
          <button
            onClick={() => {
              setAutoScroll(true);
              if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
              }
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[#8ab4f8] py-1.5 text-xs font-medium text-[#202124] hover:bg-[#aecbfa]"
          >
            <ChevronDown className="h-3.5 w-3.5" />
            New messages
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-[#3c4043] px-4 py-2">
        <div className="flex items-center gap-3 text-xs text-[#9aa0a6]">
          <span>{finalLines.length} lines</span>
          <span>•</span>
          <span>{wordCount} words</span>
        </div>
      </div>
    </div>
  );
};

export default TranscriptPanel;
